import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getAdminClients, getCaregivers, getExpiringPriorAuths, deleteClient } from '../utils/api';
import { paymentsApi, subscribe } from '../utils/paymentsStore';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, useToast, fmtDate, fmtMoney } from '../components/ui';

export default function ClientsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState(null);
  const [users, setUsers] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [claims, setClaims] = useState([]);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showToast, toastNode] = useToast();
  const q = params.get('q') || '';
  const tab = params.get('tab') || 'active';
  const { sort, onSort, apply } = useSort('clientName', 'asc');

  useEffect(() => {
    Promise.all([getAdminClients(), getCaregivers().catch(() => ({ users: [] })), getExpiringPriorAuths().catch(() => ({ authorizations: [] }))])
      .then(([c, u, a]) => { setClients(c.clients || []); setUsers(u.users || []); setExpiring(a.authorizations || []); })
      .catch(e => { setError(e.message || 'Could not load clients'); setClients([]); });
  }, []);
  useEffect(() => {
    const load = () => paymentsApi.listClaims().then(setClaims);
    load(); return subscribe(load);
  }, []);

  const userName = useMemo(() => Object.fromEntries(users.map(u => [u.username, `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username])), [users]);

  const rows = useMemo(() => {
    if (!clients) return [];
    const expByClient = {};
    expiring.forEach(a => { if (!expByClient[a.clientId] || a.daysUntilExpiry < expByClient[a.clientId].daysUntilExpiry) expByClient[a.clientId] = a; });
    const openByClient = {};
    claims.forEach(c => { if (c.isOutstanding) { const k = c.clientId || `name:${c.patientName.toLowerCase()}`; openByClient[k] = (openByClient[k] || 0) + c.adjustmentCents; } });
    const needle = q.trim().toLowerCase();
    return clients
      .filter(c => (tab === 'ended' ? c.serviceStatus === 'service-ended' : c.serviceStatus !== 'service-ended'))
      .filter(c => !needle || (c.clientName || '').toLowerCase().includes(needle) || (c.medicaidId || '').toLowerCase().includes(needle))
      .map(c => ({
        ...c,
        caregivers: (c.assignedCaregivers || []).map(u => userName[u] || u),
        expiring: expByClient[c.clientId] || null,
        openCents: openByClient[c.clientId] || openByClient[`name:${(c.clientName || '').toLowerCase()}`] || 0,
      }));
  }, [clients, expiring, claims, userName, q, tab]);

  const sorted = apply(rows, (r, col) => {
    if (col === 'expiring') return r.expiring ? r.expiring.daysUntilExpiry : null;
    if (col === 'openCents') return r.openCents;
    return r[col];
  });
  const counts = useMemo(() => ({ active: (clients || []).filter(c => c.serviceStatus !== 'service-ended').length, ended: (clients || []).filter(c => c.serviceStatus === 'service-ended').length }), [clients]);

  // Same deep links the client record uses: the legacy editor opens the matching dialog.
  const manage = (open, id) => `/clients/manage?open=${open}&client=${encodeURIComponent(id)}`;
  const onDelete = async (c) => {
    try {
      await deleteClient(c.clientId);
      setClients(prev => prev.filter(x => x.clientId !== c.clientId));
      showToast(`Deleted ${c.clientName}`);
    } catch (e) { showToast(e.message || 'Could not delete client', 'error'); }
    setConfirmDelete(null);
  };

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Clients" subtitle="One record per client, shared by authorizations, payments and the Progress Notes app."
          actions={<>
            <Link to="/clients/manage" className="ops-btn">Manage (legacy view)</Link>
            <Link to="/clients/manage?open=new" className="ops-btn ops-btn--primary"><Icon name="plus" /> Add client</Link>
          </>} />
        {error && <div className="ops-error">{error}</div>}
        <Panel flush
          action={
            <div className="ops-search">
              <Icon name="search" />
              <input value={q} onChange={e => setParam('q', e.target.value)} placeholder="Search name or Medicaid ID" aria-label="Search clients" />
            </div>
          }
          title={
            <div className="ops-tabs" role="tablist">
              <button type="button" role="tab" className={tab === 'active' ? 'is-active' : ''} onClick={() => setParam('tab', '')}>Active <span className="count">{counts.active}</span></button>
              <button type="button" role="tab" className={tab === 'ended' ? 'is-active' : ''} onClick={() => setParam('tab', 'ended')}>Service ended <span className="count">{counts.ended}</span></button>
            </div>
          }>
          {!clients ? <Skeleton rows={6} /> : sorted.length === 0 ? <Empty title="No clients found">{q ? 'Try a different search.' : 'Add a client to get started.'}</Empty> : (
            <div className="ops-table-wrap">
              <table className="ops-table">
                <thead>
                  <tr>
                    <SortTh col="clientName" label="Client name" sort={sort} onSort={onSort} />
                    <SortTh col="dob" label="Date of birth" sort={sort} onSort={onSort} />
                    <th>Medicaid ID</th>
                    <th>Assigned caregivers</th>
                    <SortTh col="expiring" label="Auth ends" sort={sort} onSort={onSort} />
                    <SortTh col="openCents" label="Open claims" sort={sort} onSort={onSort} className="num" />
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(c => (
                    <tr key={c.clientId} className="is-clickable" onClick={() => navigate(`/clients/${encodeURIComponent(c.clientId)}`)}>
                      <td><Link to={`/clients/${encodeURIComponent(c.clientId)}`} className="ops-cell-main" onClick={e => e.stopPropagation()}>{c.clientName}</Link></td>
                      <td>{c.dob ? fmtDate(c.dob) : <span className="muted">None</span>}</td>
                      <td className="mono">{c.medicaidId || ''}</td>
                      <td>{c.caregivers.length ? c.caregivers.join(', ') : <span className="muted">None</span>}</td>
                      <td>{c.expiring ? <><div className="ops-cell-main">{fmtDate(c.expiring.endDate)}</div><div className={`ops-cell-sub${c.expiring.daysUntilExpiry < 0 ? ' neg' : ''}`}>{c.expiring.daysUntilExpiry < 0 ? 'expired' : `in ${c.expiring.daysUntilExpiry}d`} · {c.expiring.payer}</div></> : <span className="muted">No alert</span>}</td>
                      <td className="num">{c.openCents ? fmtMoney(c.openCents) : <span className="muted">None</span>}</td>
                      <td><StatusPill status={c.serviceStatus === 'service-ended' ? 'service-ended' : 'active'} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        {confirmDelete === c.clientId ? (
                          <div className="ops-actions ops-actions--row">
                            <span className="ops-cell-sub">Delete {c.clientName}?</span>
                            <button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={() => onDelete(c)}>Yes, delete</button>
                            <button type="button" className="ops-btn ops-btn--sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div className="ops-actions ops-actions--row">
                            <Link to={manage('edit', c.clientId)} className="ops-btn ops-btn--sm">Edit</Link>
                            <Link to={manage('assessment', c.clientId)} className="ops-btn ops-btn--sm ops-btn--navy-outline">Assessment</Link>
                            <Link to={manage('ta', c.clientId)} className="ops-btn ops-btn--sm ops-btn--teal-outline">T&amp;A Reports</Link>
                            <Link to={manage('prior-auth', c.clientId)} className="ops-btn ops-btn--sm ops-btn--gold-outline">Prior Auths</Link>
                            <button type="button" className="ops-btn ops-btn--sm ops-btn--danger" onClick={() => setConfirmDelete(c.clientId)}>Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {toastNode}
    </main>
  );
}
